import { Button } from '@progress/kendo-react-buttons';
import { SvgIcon } from '@progress/kendo-react-common';
import { caretAltLeftIcon } from '@progress/kendo-svg-icons';
import { FC, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Spinner } from '../../components/common';
import { selectCustomers } from '../../features';
import { ILocationDetails } from '../../model';
import { locationService } from '../../services';
import { useAppSelector } from '../../store/hooks';
import LocationDetailsGeneral from './LocationDetailsGeneral';
import LocationSubscriptions from './LocationSubscriptions';

import styles from './LocationDetails.module.scss';

type SectionId = 'general' | 'subscriptions';

const SECTIONS: { id: SectionId; title: string }[] = [
  { id: 'general', title: 'General' },
  { id: 'subscriptions', title: 'Subscriptions' },
];

const LocationDetails: FC = () => {
  const { locationId } = useParams<{ locationId: string }>();
  const navigate = useNavigate();
  const customerId = useAppSelector(selectCustomers).current?.id;
  const [location, setLocation] = useState<ILocationDetails | null>();
  const [selectedSection, setSelectedSection] = useState<SectionId>('general');

  useEffect(() => {
    if (!customerId || !locationId) {
      setLocation(null);
      return;
    }

    setLocation(undefined);
    locationService
      .getLocation(locationId)
      .then((x) => setLocation(x || null))
      .catch(() => setLocation(null));
  }, [customerId, locationId]);

  if (location === undefined) {
    return <Spinner />;
  }

  const section = SECTIONS.find((x) => x.id === selectedSection) ?? SECTIONS[0];

  return (
    <div className={`page ${styles.locationDetails}`}>
      <header className={styles.header}>
        <Button className={styles.crumbButtonLink} fillMode='link' onClick={() => navigate('/locations')}>
          <SvgIcon icon={caretAltLeftIcon} />
          <span>Locations</span>
        </Button>
        <span className={styles.separator}>/</span>
        <span className={styles.currentCrumb} aria-current='page'>
          {location?.name || 'Location details not found'}
        </span>
      </header>
      {location && (
        <div className={styles.body}>
          <nav className={styles.sidebar}>
            <ul>
              {SECTIONS.map((x) => (
                <li key={x.id}>
                  <button
                    type='button'
                    aria-pressed={x.id === selectedSection}
                    className={x.id === selectedSection ? styles.active : ''}
                    onClick={() => setSelectedSection(x.id)}
                  >
                    {x.title}
                  </button>
                </li>
              ))}
            </ul>
          </nav>
          <div className={styles.content}>
            <h2 className={styles.sectionTitle}>{section.title}</h2>
            {section.id === 'general' && <LocationDetailsGeneral location={location} />}
            {section.id === 'subscriptions' && <LocationSubscriptions key={location.id} locationId={location.id} />}
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationDetails;
